import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useWiringAudit } from '../../hooks/useAdminDashboard';
import Spinner from '../../components/common/Spinner';
import { routes } from '../../lib/routes';
import { MdOutlineArrowForward, MdRefresh } from 'react-icons/md';
import { CheckCircle, WarningCircle, Xmark } from 'iconoir-react';

const getColorClass = (color: string) => {
  switch (color) {
    case 'GREEN':
      return {
        bg: 'bg-green-500/10',
        border: 'border-green-500/30',
        textClass: 'text-green-600',
        dot: 'bg-green-500',
      };
    case 'YELLOW':
      return {
        bg: 'bg-yellow-500/10',
        border: 'border-yellow-500/30',
        textClass: 'text-yellow-600',
        dot: 'bg-yellow-500',
      };
    case 'RED':
      return {
        bg: 'bg-red-500/10',
        border: 'border-red-500/30',
        textClass: 'text-red-600',
        dot: 'bg-red-500',
      };
    default:
      return {
        bg: 'bg-gray-100',
        border: 'border-gray-200',
        textClass: 'text-gray-600',
        dot: 'bg-gray-400',
      };
  }
};

const getOverallStatusDisplay = (status: string, color: string) => {
  const colorClass = getColorClass(color);

  switch (status) {
    case 'HEALTHY':
      return {
        icon: <CheckCircle className="w-8 h-8 text-green-500" />,
        text: 'All Systems Operational',
        ...colorClass,
      };
    case 'PARTIAL':
      return {
        icon: <WarningCircle className="w-8 h-8 text-yellow-500" />,
        text: 'Partial Issues Detected',
        ...colorClass,
      };
    case 'BROKEN':
    case 'ERROR':
      return {
        icon: <Xmark className="w-8 h-8 text-red-500" />,
        text: 'System Issues',
        ...colorClass,
      };
    case 'NO_USERS':
      return {
        icon: <WarningCircle className="w-8 h-8 text-gray-500" />,
        text: 'No Users to Audit',
        bg: 'bg-gray-100',
        border: 'border-gray-200',
        textClass: 'text-gray-600',
        dot: 'bg-gray-400',
      };
    default:
      return {
        icon: <Spinner size="sm" />,
        text: 'Loading...',
        bg: 'bg-gray-100',
        border: 'border-gray-200',
        textClass: 'text-gray-600',
        dot: 'bg-gray-400',
      };
  }
};

const PipelineCheckCard = ({
  title,
  check,
  isLoading
}: {
  title: string;
  check?: { status: string; color: string; [key: string]: any };
  isLoading: boolean;
}) => {
  if (isLoading) {
    return (
      <div className="p-4 bg-white rounded-xl border border-gray-200">
        <div className="flex items-center gap-3">
          <Spinner size="sm" />
          <span className="text-gray-500">{title}</span>
        </div>
      </div>
    );
  }

  if (!check) {
    return (
      <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-gray-300"></div>
          <span className="text-gray-500">{title}</span>
          <span className="ml-auto text-xs text-gray-400">Not checked</span>
        </div>
      </div>
    );
  }

  const colorClass = getColorClass(check.color);

  return (
    <div className={`p-4 rounded-xl border ${colorClass.bg} ${colorClass.border}`}>
      <div className="flex items-center gap-3">
        <div className={`w-3 h-3 rounded-full ${colorClass.dot}`}></div>
        <span className={`font-medium ${colorClass.textClass}`}>{title}</span>
        <span className={`ml-auto text-xs font-semibold ${colorClass.textClass}`}>
          {check.status}
        </span>
      </div>
      {/* Show additional details if available */}
      {check.count !== undefined && (
        <p className="mt-2 text-sm text-gray-600">
          Count: {check.count}
        </p>
      )}
      {check.slots_filled !== undefined && (
        <p className="mt-1 text-sm text-gray-600">
          Slots: {check.slots_filled}/{check.slots_expected}
        </p>
      )}
      {check.match_count !== undefined && (
        <p className="mt-1 text-sm text-gray-600">
          Matches: {check.match_count}
        </p>
      )}
    </div>
  );
};

const AdminHealthPage = () => {
  const navigate = useNavigate();
  const { data: auditData, isLoading, refetch, isFetching } = useWiringAudit();
  const audit = auditData?.result;

  const overallStatus = getOverallStatusDisplay(
    audit?.overall_status || 'loading',
    audit?.color || 'GRAY'
  );

  return (
    <div className="px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="!font-medium text-heading-500 mb-1">Wiring Audit</h3>
          <p className="text-sm text-body-400 font-medium mb-0">
            Truth-based health check of the latest user journey.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 disabled:opacity-50"
          >
            <MdRefresh className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => navigate(routes.adminMatching)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 rounded-lg text-sm font-medium text-white"
          >
            Matching Diagnostics
            <MdOutlineArrowForward className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Overall Status Banner */}
      <div className={`p-6 rounded-xl border ${overallStatus.bg} ${overallStatus.border} mb-6`}>
        <div className="flex items-center gap-4">
          {overallStatus.icon}
          <div className="flex-1">
            <h4 className="font-semibold text-gray-800 text-lg">{overallStatus.text}</h4>
            {audit?.timestamp && (
              <p className="text-sm text-gray-500">
                Last checked: {new Date(audit.timestamp).toLocaleString()}
              </p>
            )}
          </div>
          {audit?.efficiency !== undefined && (
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-800">{audit.efficiency}%</p>
              <p className="text-sm text-gray-500">Efficiency</p>
            </div>
          )}
        </div>

        {/* Latest User Info */}
        {audit?.latest_user && (
          <div className="mt-4 p-3 bg-white/50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-1">Most Recently Active User:</p>
            <p className="text-sm text-gray-600">
              {audit.latest_user.name || audit.latest_user.email}
              <span className="text-gray-400 ml-2">({audit.latest_user.onboarding_status})</span>
            </p>
            {audit.latest_user.last_active_at && (
              <p className="text-xs text-gray-400 mt-1">
                Last active: {new Date(audit.latest_user.last_active_at).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {/* Issues */}
        {audit?.issues && audit.issues.length > 0 && (
          <div className="mt-4 p-3 bg-white/50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-2">Issues:</p>
            <ul className="list-disc list-inside text-sm text-gray-600">
              {audit.issues.map((issue: string, idx: number) => (
                <li key={idx}>{issue}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      {audit?.summary && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-gray-50 rounded-xl text-center">
            <p className="text-2xl font-bold text-gray-800">{audit.summary.total_checks}</p>
            <p className="text-sm text-gray-500">Total Checks</p>
          </div>
          <div className="p-4 bg-green-50 rounded-xl text-center">
            <p className="text-2xl font-bold text-green-600">{audit.summary.healthy}</p>
            <p className="text-sm text-green-600">Healthy</p>
          </div>
          <div className="p-4 bg-yellow-50 rounded-xl text-center">
            <p className="text-2xl font-bold text-yellow-600">{audit.summary.partial}</p>
            <p className="text-sm text-yellow-600">Partial</p>
          </div>
          <div className="p-4 bg-red-50 rounded-xl text-center">
            <p className="text-2xl font-bold text-red-600">{audit.summary.broken}</p>
            <p className="text-sm text-red-600">Broken</p>
          </div>
        </div>
      )}

      {/* Pipeline Status Cards */}
      <h4 className="font-semibold text-gray-800 mb-4">Pipeline Status</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <PipelineCheckCard
          title="Basic Embeddings"
          check={audit?.pipeline_status?.basic_embeddings}
          isLoading={isLoading}
        />
        <PipelineCheckCard
          title="Multi-Vector Embeddings"
          check={audit?.pipeline_status?.multi_vector_embeddings}
          isLoading={isLoading}
        />
        <PipelineCheckCard
          title="Intent Classification"
          check={audit?.pipeline_status?.intent_classification}
          isLoading={isLoading}
        />
        <PipelineCheckCard
          title="Matches"
          check={audit?.pipeline_status?.matches}
          isLoading={isLoading}
        />
        <PipelineCheckCard
          title="Matching Algorithm"
          check={audit?.pipeline_status?.matching_algorithm}
          isLoading={isLoading}
        />
      </div>

      {/* Totals */}
      {audit?.totals && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-blue-50 rounded-xl">
            <p className="text-sm text-blue-600 mb-1">Completed Users</p>
            <p className="text-2xl font-bold text-blue-700">{audit.totals.completed_users}</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-xl">
            <p className="text-sm text-purple-600 mb-1">Total Matches</p>
            <p className="text-2xl font-bold text-purple-700">{audit.totals.total_matches}</p>
          </div>
          <div className="p-4 bg-red-50 rounded-xl">
            <p className="text-sm text-red-600 mb-1">Broken Users</p>
            <p className="text-2xl font-bold text-red-700">{audit.totals.broken_users_count || 0}</p>
          </div>
        </div>
      )}

      {/* Broken Users Section */}
      {audit?.broken_users && audit.broken_users.length > 0 && (
        <div className="mb-6">
          <h4 className="font-semibold text-red-700 mb-4">
            ⚠️ Broken Users ({audit.broken_users.length})
          </h4>
          <p className="text-sm text-gray-600 mb-3">
            These users have onboarding_status=completed but are missing profile/persona data.
            They will be stuck and unable to access the platform.
          </p>
          <div className="bg-red-50 rounded-xl border border-red-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-red-100">
                <tr>
                  <th className="px-4 py-2 text-left text-red-700">Name</th>
                  <th className="px-4 py-2 text-left text-red-700">Email</th>
                  <th className="px-4 py-2 text-left text-red-700">Issue</th>
                  <th className="px-4 py-2 text-left text-red-700">Created</th>
                </tr>
              </thead>
              <tbody>
                {audit.broken_users.map((user: any) => (
                  <tr key={user.id} className="border-t border-red-100">
                    <td className="px-4 py-2 text-gray-800">{user.name || 'Unknown'}</td>
                    <td className="px-4 py-2 text-gray-600">{user.email}</td>
                    <td className="px-4 py-2">
                      <span className="px-2 py-1 bg-red-200 text-red-700 rounded text-xs">
                        {user.issue}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Footer Info */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
        <p className="mb-2">
          <strong>Status indicators:</strong>
        </p>
        <ul className="flex gap-6">
          <li className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            GREEN - Pipeline step working
          </li>
          <li className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            YELLOW - Partial/degraded
          </li>
          <li className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            RED - Broken/missing
          </li>
        </ul>
      </div>
    </div>
  );
};

export default AdminHealthPage;
